import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { leadSourceListQueryFn, leadStatusListQueryFn } from "@gymflow/api";
import { LUXON_DATE_FORMAT } from "@gymflow/helpers";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

import useGymflowModels from "../../../store";
import { Button, PaginatedSelect, SlideSideBar } from "../../atoms";
import { DatePicker } from "../../molecules";

type SelectOption<T extends number | string = number> = {
  label: string;
  value: T;
};

export type LeadFilters = {
  createdFrom?: string;
  createdTo?: string;
  leadStatus?: SelectOption[];
  leadSource?: SelectOption[];
  emailCommunication?: boolean;
  smsCommunication?: boolean;
};

export interface LeadsFilterSidebarProps {
  isVisible: boolean;
  onVisibilityChange: (newVisibility: boolean) => void;
  value: LeadFilters;
  onChange: (newFilters: LeadFilters) => void;
  includeDealClosedAndList: boolean;
}

const enabledDisabledOptions = [
  { label: "Enabled", value: true },
  { label: "Disabled", value: false },
] satisfies { label: string; value: boolean }[];

export const LeadsFilterSidebar = ({
  value,
  onChange,
  isVisible,
  onVisibilityChange,
  includeDealClosedAndList = false,
}: LeadsFilterSidebarProps) => {
  const [filters, setFilters] = useState(value);
  const hide = () => {
    onVisibilityChange(false);
  };

  useEffect(() => {
    setFilters(value);
  }, [value]);

  const { api } = useGymflowModels();

  return (
    <SlideSideBar isOpen={isVisible} hide={hide}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex flex-col justify-between border-b border-gray-200 px-6 py-4">
          <div className="mb-1 flex flex-row items-center justify-between">
            <div className="text-xl font-semibold text-gray-900">Filters</div>
            <FontAwesomeIcon
              onClick={() => {
                hide();
              }}
              className="cursor-pointer text-xl text-gray-600"
              icon={faClose}
            />
          </div>
          <div className="text-sm font-medium text-gray-600">
            Apply one or more filters
          </div>
        </div>

        <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
          <div className="flex flex-col">
            <DatePicker
              label="Lead Created Date"
              mode="range"
              labelClassName="text-gray-700"
              isClearable
              selected={dateStringRangeToDateRange(
                filters.createdFrom,
                filters.createdTo,
              )}
              handleDateSave={(newRange) => {
                if (!newRange) {
                  setFilters((previous) => ({
                    ...previous,
                    createdFrom: undefined,
                    createdTo: undefined,
                  }));
                  return;
                }
                if (!newRange.from) return;
                const createdFrom = DateTime.fromJSDate(
                  newRange.from ?? newRange.to,
                ).toFormat(LUXON_DATE_FORMAT);
                const createdTo = DateTime.fromJSDate(
                  newRange.to ?? newRange.from,
                ).toFormat(LUXON_DATE_FORMAT);
                setFilters((previous) => ({
                  ...previous,
                  createdFrom,
                  createdTo,
                }));
              }}
            />
          </div>
          <div>
            <PaginatedSelect
              className="h-10 !rounded-[.625rem] border-gray-200"
              placeholder="Lead Status"
              value={filters.leadStatus}
              isMulti
              onChange={(newValue) => {
                setFilters({ ...filters, leadStatus: newValue });
              }}
              refetchOnMenuOpen
              loadOptions={async () => {
                const data = await leadStatusListQueryFn({
                  api,
                });

                return {
                  options: data
                    .filter((status) => {
                      if (includeDealClosedAndList) {
                        return true;
                      }
                      return (
                        status.presetType !== "DEAL_CLOSED" &&
                        status.presetType !== "DEAL_LOST"
                      );
                    })
                    .map((status) => ({
                      label: status.name,
                      value: status.id,
                    })),
                  hasMore: false,
                };
              }}
            />
          </div>
          <div>
            <PaginatedSelect
              className="h-10 !rounded-[.625rem] border-gray-200"
              placeholder="Lead Source"
              value={filters.leadSource}
              isMulti
              onChange={(newValue) => {
                setFilters({ ...filters, leadSource: newValue });
              }}
              loadOptions={async (_, __, { page }) => {
                const data = await leadSourceListQueryFn({
                  api,
                  opts: {
                    page,
                    extraParams: {
                      active: true,
                    },
                  },
                });

                return {
                  options: data.content.map((source) => ({
                    label: source.name,
                    value: source.id,
                  })),
                  hasMore: !data.last,
                  additional: {
                    page: page + 1,
                  },
                };
              }}
            />
          </div>
          <div>
            <PaginatedSelect
              className="h-10 !rounded-[.625rem] border-gray-200"
              placeholder="Email Communication"
              isClearable
              value={enabledDisabledOptions.find(
                (e) => e.value === filters?.emailCommunication,
              )}
              onChange={(newValue) => {
                setFilters((previous) => ({
                  ...previous,
                  emailCommunication: newValue?.value,
                }));
              }}
              loadOptions={async () => {
                return {
                  options: enabledDisabledOptions,
                };
              }}
            />
          </div>
          <div>
            <PaginatedSelect
              className="h-10 !rounded-[.625rem] border-gray-200"
              placeholder="SMS Communication"
              isClearable
              value={enabledDisabledOptions.find(
                (e) => e.value === filters?.smsCommunication,
              )}
              onChange={(newValue) => {
                setFilters((previous) => ({
                  ...previous,
                  smsCommunication: newValue?.value,
                }));
              }}
              loadOptions={async () => {
                return {
                  options: enabledDisabledOptions,
                };
              }}
            />
          </div>
        </div>
        <div className="flex h-20 flex-row items-center justify-end border-t border-gray-200 px-6">
          <Button onClick={() => hide()} className="mr-2 mt-2 w-full">
            Cancel
          </Button>
          <Button
            intent="primary"
            className="mt-2 w-full"
            onClick={() => {
              onChange(filters);
              hide();
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </SlideSideBar>
  );
};

const dateStringRangeToDateRange = (from?: string, to?: string) => {
  if (!from) return;
  return {
    from: DateTime.fromFormat(from, LUXON_DATE_FORMAT)
      .setZone("local", { keepLocalTime: true })
      .toJSDate(),
    ...(to
      ? {
          to: DateTime.fromFormat(to, LUXON_DATE_FORMAT)
            .setZone("local", { keepLocalTime: true })
            .toJSDate(),
        }
      : {}),
  };
};
